import type { BrandKit } from '@/hooks/useBrandKits';

/**
 * The client-side mirror of the SQL rule in
 * `resolve_effective_brand_kit_id(p_actor)` (migration
 * 20260822190910_workspace_resolver_one_rule_two_entry_points.sql).
 *
 * 🔴 THIS IS A DELIBERATE SECOND IMPLEMENTATION OF ONE RULE, NOT A SECOND RULE.
 *
 * It exists because the SQL entry point the browser can call
 * (`get_effective_brand_kit()`) returns the resolved row, not the ordering, and
 * the sidebar already holds the full kit list --- re-fetching one row on every
 * render to learn something the client can compute is a round trip for nothing.
 *
 * The danger is obvious and is the exact failure this whole workstream exists
 * to fix: two judges of one state that drift apart. It is contained two ways:
 *
 *   1. The rule is written ONCE here, as a pure function, and every client
 *      caller goes through it. No component re-derives "which kit is active."
 *   2. `pickActiveWorkspace.test.ts` asserts this function agrees with the SQL
 *      for every ordering case. If you change one, that test fails until you
 *      change the other. **Do not change this file without changing the
 *      migration, and vice versa.**
 *
 * The rule, in both languages:
 *   1. this USER's own pointer (`user_profiles.active_brand_kit_id`)
 *   2. then the ACCOUNT's default (`brand_kits.is_default`)
 *   3. then most recently updated
 *   4. then lowest id, purely so the answer is total rather than arbitrary
 * Only `is_active` kits are eligible; archived ones are never returned. When the
 * caller supplies the resolved account owner id, only that account's kits are
 * eligible (the SQL's `k.user_id = resolve_account_owner(p_actor)`).
 */
export function pickActiveWorkspace(
  kits: readonly BrandKit[],
  pointerId: string | null | undefined,
  accountOwnerId?: string | null,
): BrandKit | null {
  const eligible = eligibleWorkspaces(kits, accountOwnerId);
  if (eligible.length === 0) return null;

  if (pointerId) {
    const pinned = eligible.find((k) => k.id === pointerId);
    // A pointer at an archived or foreign kit falls through rather than
    // resolving to nothing --- the DB trigger prevents writing one, but a kit
    // archived AFTER the pointer was set is a real, reachable state.
    if (pinned) return pinned;
  }

  return [...eligible].sort(compareWorkspaceRank)[0] ?? null;
}

/**
 * The ONE eligibility rule, shared by the resolver above and by every switcher
 * that lists workspaces to choose from.
 *
 * The SQL resolver only considers `k.user_id = resolve_account_owner(actor)`.
 * `brand_kits` RLS lets a member see EVERY kit they own or are granted, so a
 * member who also has a personal account lists their own default kit beside
 * the team's; without this filter the tie-break (equal `updated_at`, then
 * lowest id) can pick the personal one and every workspace-scoped surface
 * reads as if the team's store were not connected.
 *
 * A switcher that lists kits this rule rejects offers the member a choice the
 * resolver will silently ignore, so the list and the resolver must agree.
 * With no owner id supplied only `is_active` applies; callers that cannot yet
 * (or cannot) resolve the owner must fail closed themselves rather than lean
 * on that fallback.
 */
export function eligibleWorkspaces(
  kits: readonly BrandKit[],
  accountOwnerId?: string | null,
): BrandKit[] {
  return kits.filter(
    (k) => k.is_active !== false && (!accountOwnerId || k.user_id === accountOwnerId),
  );
}

/** Steps 2-4 of the rule above. Exported so the parity test can drive it directly. */
export function compareWorkspaceRank(a: BrandKit, b: BrandKit): number {
  if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;

  const at = Date.parse(a.updated_at ?? '') || 0;
  const bt = Date.parse(b.updated_at ?? '') || 0;
  if (at !== bt) return bt - at;

  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
